import { type ControllerRenderProps, type FieldValues, type UseFormTrigger } from "react-hook-form";
import { RadioInputOption } from "../RadioInput";

export type StandardOptions = {
  name: string,
  label?: string | JSX.Element,
  value: string | number,
  helperText?: string | JSX.Element,
  isDisabled?: boolean
}[];

export const formatOptionsList = (
  options: Record<string, string | number> | RadioInputOption[] | string[] | undefined,
  sortList: boolean,
) => {
  const standardisedOptions: StandardOptions = [];

  if (!options) {
    return standardisedOptions;
  }

  if (Array.isArray(options)) {
    options.forEach((option) => {
      if (typeof option === "string" || typeof option === "number") {
        standardisedOptions.push({
          name: option.toString(),
          label: option.toString(),
          value: option,
        });
      } else {
        standardisedOptions.push(option);
      }
    });
  } else {
    Object.keys(options).forEach((key) => {
      standardisedOptions.push(
        {
          name: key,
          label: options[key].toString(),
          value: options[key]
        });
    });
  }

  if (!sortList) {
    return standardisedOptions;
  }

  // Sort options with numeric priority and put "Other" at the end
  // eslint-disable-next-line max-statements
  return standardisedOptions.sort((a, b) => {
    const labelA = (a.label ?? a.name).toString();
    const labelB = (b.label ?? b.name).toString();

    // Place "Other" at the end (case-insensitive)
    if (labelA.toLowerCase() === "other" && labelB.toLowerCase() !== "other") {
      return 1;
    }
    if (labelB.toLowerCase() === "other" && labelA.toLowerCase() !== "other") {
      return -1;
    }

    // If both values can be parsed as numbers, sort numerically
    const numA = Number(labelA);
    const numB = Number(labelB);
    if (!isNaN(numA) && !isNaN(numB)) {
      return numA - numB;
    }

    // Otherwise, sort alphabetically case-insensitive
    return labelA.toLowerCase().localeCompare(labelB.toLowerCase());
  });
};

export const updateFieldWithValidation = <T, >(
  { field, value, trigger, fieldName, afterOnChange }: {
    field: ControllerRenderProps<FieldValues>,
    value: T,
    trigger: UseFormTrigger<FieldValues>,
    fieldName: string,
    afterOnChange?: (value: T) => void,
  }
) => {
  field.onChange(value);
  trigger(fieldName);
  afterOnChange?.(value);
};

